import { validateUUID } from '@shared/utils/validate-uuid.util';
import { DecodeUrlSlug } from '../types';
import { HttpErrorResponse } from '@angular/common/http';
import { SPECIAL_CHARACTER_PATTERN } from '../constants';

export function generateUUIDv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function encodeUUIDToUrlSafe(uuid: string): string {
  /*
   * @NOTE: The regex is defined here to avoid recompilation on each function call
   * Remove hyphens from UUID
   * */
  const _hex = uuid.replace(/-/g, '');
  /*
   * @NOTE: Convert's hex to bytes
   * The regex /.{1,2}/g splits the hex string into pairs of characters (each pair represents a byte)
   * The map function converts each pair from hex to a decimal byte using parseInt with a radix of 16
   * The Uint8Array constructor creates an array of 8-bit unsigned integers from the byte values
   * */
  const _bytes = new Uint8Array(
    _hex.match(/.{1,2}/g)!.map((b) => parseInt(b, 16)),
  );
  // Encode bytes to base64
  const _base64 = btoa(String.fromCharCode(..._bytes));
  // Make it URL safe
  return _base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeURLSAFETOUUID(encoded: string): string {
  /*
   * @NOTE: The regex is defined here to avoid recompilation on each function call
   *  The regex captures the 5 groups of a UUID for formatting
   * UUID format: 8-4-4-4-12
   * Example: 123e4567-e89b-12d3-a456-426614174000
   * The regex breakdown:
   * ^ asserts position at start of the string
   * (.{8}) captures exactly 8 characters (first segment)
   * (.{4}) captures exactly 4 characters (second segment)
   * (.{4}) captures exactly 4 characters (third segment)
   * (.{4}) captures exactly 4 characters (fourth segment)
   * (.{12}) captures exactly 12 characters (fifth segment)
   * $ asserts position at end of the string
   * The replacement pattern '$1-$2-$3-$4-$5' inserts hyphens between the captured groups
   * */
  const _uuidRegex = new RegExp(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/);
  let base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) base64 += '=';
  const binary = atob(base64);
  const hex = Array.from(binary, (c) =>
    c.charCodeAt(0).toString(16).padStart(2, '0'),
  ).join('');
  // Use regex to format as UUID
  return hex.replace(_uuidRegex, '$1-$2-$3-$4-$5');
}

export function decodeUrlSlug(urlSlug: string): DecodeUrlSlug | null {
  const _slugRegex = new RegExp(/^(.+?)~([A-Za-z0-9\-_]+)$/);

  if (!urlSlug) {
    return null;
  }
  const match = urlSlug.match(_slugRegex);
  if (match) {
    const [, name, encodedId] = match;
    return {
      propName: name,
      UUID: decodeURLSAFETOUUID(encodedId),
    };
  }
  return null;
}

/**
 * Generates a URL slug by encoding the property name and UUID.
 * The property name is trimmed and spaces are replaced with hyphens.
 * The UUID is encoded to a URL-safe format.
 *
 * @param propName - The property name to be included in the slug.
 * @param uuid - The UUID to be encoded and included in the slug.
 * @returns A URL slug in the format "prop-name~encoded-uuid" or an error message if the UUID is invalid.
 */
export function urlSlugEncoder(propName: string, uuid: string) {
  const _strRegex = new RegExp(/\s+/g);
  if (!validateUUID(uuid)) {
    return 'Provide a valid UUID';
  }
  const _stringValue = propName.trim().replace(_strRegex, '-'); // Replace spaces with hyphens
  // Encode UUID to URL safe format
  const _uuid = encodeUUIDToUrlSafe(uuid);

  return `${_stringValue}~${_uuid}`;
}

/**
 * A 403 with an HTML body never reached the app - the gateway's WAF rejected
 * the request outright, usually over special characters in the payload.
 * There's no permissions problem to report, just content it can't carry.
 */
export function isGatewayBlocked(error: HttpErrorResponse): boolean {
  return (
    error.headers.get('content-type')?.toLowerCase().includes('text/html') ??
    false
  );
}

/**
 * Checked against the pre-transform body - ErrorInterceptor runs before
 * HttpRequestDataInterceptor in the chain, so `request.body` here still
 * carries the raw characters the user typed.
 */
export function bodyContainsSpecialCharacters(body: unknown): boolean {
  if (typeof body === 'string') {
    return SPECIAL_CHARACTER_PATTERN.test(body);
  }
  if (Array.isArray(body)) {
    return body.some((item) => bodyContainsSpecialCharacters(item));
  }
  if (body !== null && typeof body === 'object') {
    return Object.values(body).some((value) =>
      bodyContainsSpecialCharacters(value),
    );
  }
  return false;
}
